import { useResponseTypeClasses } from '@heidi/answer-kit';
import { TypographyP2 } from '@heidi/design-system/typography';
import { cn } from '@heidi/utils';
import { MousePointer2 } from 'lucide-react';
import { Children, type ReactNode } from 'react';
import { useIntl } from 'react-intl';

import { computerUseMessages } from '../../../data/projects/components/ComputerUseTaskBlock.status.utils';
import type { ComputerUsePlanPhase } from './ComputerUsePlanCard.utils';
import { ComputerUsePlanChecklist } from './ComputerUsePlanChecklist';
import { PlanCardFrame } from './PlanCardFrame';

export interface ComputerUsePlanCardProps {
  title: string;
  steps?: readonly string[];
  phase: ComputerUsePlanPhase;
  headerAction?: ReactNode;
  children?: ReactNode;
  /** Approval actions; only an awaiting card shows the screen-control note beside them. */
  footer?: ReactNode;
  /** Keeps the body open while it holds a mid-task approval. */
  bodyLockedOpen?: boolean;
  /** Hide the icon tile when a parent row already names this tool. */
  labelledByRow?: boolean;
  testId?: string;
}

// Approval and execution share the plan chrome so one task does not look like two.
export const ComputerUsePlanCard = ({
  title,
  steps,
  phase,
  headerAction,
  children,
  footer,
  bodyLockedOpen,
  labelledByRow,
  testId = 'computer-use-plan-card',
}: ComputerUsePlanCardProps): JSX.Element => {
  const intl = useIntl();
  // The note says what the card is about to do, so it is `meta`, not content.
  const { meta } = useResponseTypeClasses();

  return (
    <PlanCardFrame
      title={title}
      phase={phase}
      stepCount={steps?.length ?? 0}
      runningStatus={intl.formatMessage(computerUseMessages.planRunning)}
      steps={<ComputerUsePlanChecklist steps={steps} phase={phase} />}
      hasBody={Boolean(steps?.length) || Children.toArray(children).length > 0}
      headerAction={headerAction}
      footer={footer}
      footerNote={
        phase === 'awaiting' ? (
          <div className="flex min-w-0 flex-1 basis-64 items-start gap-3">
            <MousePointer2
              className="mt-0.5 size-4 shrink-0 text-text-secondary"
              aria-hidden
            />
            <TypographyP2 className={cn('text-text-secondary', meta)}>
              {intl.formatMessage(computerUseMessages.planApprovalNote)}
            </TypographyP2>
          </div>
        ) : undefined
      }
      resetKey={`${title}:${steps?.join('|') ?? ''}`}
      bodyLockedOpen={bodyLockedOpen}
      labelledByRow={labelledByRow}
      testId={testId}
    >
      {children}
    </PlanCardFrame>
  );
};
